<script lang="ts" setup>
import { useArticleStore } from '@/stores';
import Article from './components/Article.vue'
import ClassLink from './components/ClassLink.vue'
import Share from '@/components/Share.vue'
import type { ArticleBlog } from '@/utils/types/Article'
import useSeo from './seo';

const route = useRoute()
const slug = route.params.slug as string
const articleStore = useArticleStore()
const blog = computed(() => articleStore.blog)
const recommends = computed(() => articleStore.recommends)
if (articleStore.blogCategories.length === 0) {
  articleStore.fetchArticleCategories('blogCategories', {
    className: 'articleBlog'
  })
}

await articleStore.fetchArticleBlog(slug)
const category = blog.value?.categories?.[0]
const mainCategory = computed(() => {
  const lookupId = category?.parent_id || category?.id
  return articleStore.blogCategories.find(o => o.id === lookupId)
})

onMounted(() => {
  nextTick(() => {
    if (blog.value !== null) {
      articleStore.fetchArticleRecommends(blog.value.id as string)
    }
  })
})

useSeo(blog.value as ArticleBlog)
</script>

<template>
  <div class="content">
    <div class="content__leftbox">
      <div class="content__leftbox__inner">
        <div class="content__date__box">
          <Date class="content__date" :date="blog?.publish_date" color="dark" size="small" />
        </div>
        <ClassLink class="content__classlink" :main-category="mainCategory" :current-sub-category-id="category?.id" />
        <Share class="content__share__pc" :border="false" :round="true" />
      </div>
    </div>
    <div class="content__rightbox">
      <div class="content__texteditor">
        <figure class="content__banner">
          <Nimg :src="blog?.image?.link" :title="blog?.image?.title" :alt="blog?.image?.alt" format="webp" />
        </figure>
        <h1 class="content__title">{{ blog?.subject }}</h1>
        <div class="content__box">
          <Block :blocks="blog?.blocks" />
        </div>
      </div>
      <div class="content__share__mobile">
        <Share />
      </div>
      <Article :recommends="recommends"></Article>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.content {
  @include grid;

  @include max-media(960) {
    grid-template-columns: repeat(4, 1fr);
  }

  &__leftbox {
    position: relative;
    grid-column: 1 / span 2;

    @include min-media(961) {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
    }

    @include max-media(960) {
      grid-column: 1 / span 4;
      // margin-bottom: toRem(30);
    }

    &__inner {
      display: flex;
      flex-direction: column;
      padding: toRem(150) 0;
      gap: toRem(15);

      @include min-media(961) {
        position: fixed;
        align-items: flex-end;
      }

      @include max-media(960) {
        align-items: center;
        padding: toRem(15) 0;
      }
    }
  }

  &__rightbox {
    grid-column: 3 / span 8;

    @include max-media(1024) {
      grid-column: 3 / span 10;
    }

    @include max-media(960) {
      grid-column: 1 / span 4;
    }
  }

  &__date {

    @include max-media(960) {
      margin-bottom: toRem(15);
    }
  }

  &__share {
    &__pc {
      @include max-media(960) {
        display: none;
      }
    }

    &__mobile {
      @include min-media(961) {
        display: none;
      }

      display: flex;
      justify-content: center;
      padding: toRem(50) 0;
    }
  }

  &__texteditor {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    column-gap: toRem(24);
    margin-bottom: toRem(50);

    @include max-media(960) {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  &__banner {
    grid-column: 2 / span 6;
    margin-bottom: toRem(30);

    @include max-media(960) {
      grid-column: 1 / span 4;
      margin-left: toRem(-20);
      margin-right: toRem(-20);
    }

    img {
      display: block;
      width: 100%;
    }
  }

  &__title {
    grid-column: 2 / span 6;
    font-size: toRem(28);
    font-weight: 500;
    margin-bottom: toRem(50);

    @include max-media(960) {
      grid-column: 1 / span 4;
    }

    @include max-media(480) {
      font-size: toRem(24);
    }
  }

  &__box {
    grid-column: 2 / span 6;

    @include max-media(960) {
      grid-column: 1 / span 4;
    }
  }
}
</style>
